import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BellRing,
  ChevronRight,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
  ShieldOff,
  ShieldAlert,
  Send,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import RequireAdmin from "@/components/admin/RequireAdmin";
import AdminShell from "@/components/admin/AdminShell";
import { useAdminI18n } from "@/lib/adminI18n";
import {
  ADMIN_PLACEHOLDER,
  extractApiMessage,
  extractCustomerIdentity,
  formatAdminDate,
  formatLanguageLabel,
  readJsonSafe,
  verificationLabel,
  withPlaceholder,
} from "@/lib/adminDisplay";

const STATUS_OPTIONS = [
  { id: "", labelKey: "all", fallback: "All" },
  { id: "active", labelKey: "active", fallback: "Active" },
  { id: "inactive", labelKey: "inactive", fallback: "Inactive" },
  { id: "suspended", labelKey: "suspended", fallback: "Suspended" },
  { id: "frozen", labelKey: "frozen", fallback: "Frozen" },
  { id: "deleted", labelKey: "deleted", fallback: "Deleted" },
];

const STATUS_BADGE = {
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  inactive: "bg-white/10 text-white/70 border-white/20",
  suspended: "bg-red-500/15 text-red-300 border-red-500/30",
  frozen: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  deleted: "bg-red-500/20 text-red-200 border-red-500/35",
  closed: "bg-red-500/20 text-red-200 border-red-500/35",
};

function normalizeVerificationCode(value) {
  const normalizedDigits = String(value || "")
    .replace(/[\u0660-\u0669]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0));
  return normalizedDigits.replace(/\D/g, "").slice(0, 6);
}

function StatusBadge({ status }) {
  const { t } = useAdminI18n();
  const s = (status || "active").toLowerCase();
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS_BADGE[s] || STATUS_BADGE.active}`}
      data-testid={`customer-status-badge-${s}`}
    >
      {t(`customers.${s}`, s)}
    </span>
  );
}

function VerificationBadge({ verification }) {
  const { t } = useAdminI18n();
  const raw = verificationLabel(verification);
  const label = raw === "Verified"
    ? t("customers.verified", raw)
    : raw === "Unverified"
      ? (t("customers.unverified", "Unverified"))
      : (t("customers.notAvailable", "Not available"));
  const tone = verification === true
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
    : verification === false
      ? "bg-amber-500/15 text-amber-200 border-amber-500/30"
      : "bg-white/10 text-white/65 border-white/20";

  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone}`}>
      {label}
    </span>
  );
}

function accountSourceLabel(value, t) {
  const source = (value || "").toLowerCase();
  if (source === "customer") return t("customers.sourceCustomer");
  if (source === "legacy_user" || source === "users") return t("customers.sourceLegacyUser");
  return t("customers.sourceUnknown");
}

function RequestStatusBadge({ status }) {
  const { t } = useAdminI18n();
  const value = (status || "pending").toLowerCase();
  const tone = {
    pending: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    reviewed: "bg-sky-500/15 text-sky-200 border-sky-500/30",
    completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    frozen: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    rejected: "bg-red-500/15 text-red-300 border-red-500/30",
    cancelled: "bg-white/10 text-white/65 border-white/20",
    failed: "bg-red-500/15 text-red-300 border-red-500/30",
    code_requested: "bg-[#F37021]/15 text-[#F7A76F] border-[#F37021]/30",
    draft: "bg-white/10 text-white/65 border-white/20",
  }[value] || "bg-white/10 text-white/65 border-white/20";

  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}>
      {t(`customers.requestStatus.${value}`, value.replace(/_/g, " "))}
    </span>
  );
}

function CustomerIdPill({ value }) {
  const { t } = useAdminI18n();
  const id = (value || "").trim();
  const isCustomerId = /^CUST-\d{6,7}$/i.test(id);
  return (
    <span className="rounded-full border border-[#F37021]/35 bg-[#F37021]/12 px-2 py-0.5 font-mono text-[10px] text-[#F7A76F]">
      {id ? (isCustomerId ? id : `${t("customers.internalId", "Internal ID")}: ${id}`) : ADMIN_PLACEHOLDER}
    </span>
  );
}

function deletionReasonLabel(item, t) {
  const raw = item?.reason_label || item?.reason || "";
  const value = String(raw || "").trim();
  if (!value) return ADMIN_PLACEHOLDER;
  if (value.toLowerCase() === "other") return t("customers.reasonOther", "Other");
  return value;
}

function Field({ label, value, mono = false, full = false, testid }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] tracking-wider uppercase text-white/40 mb-1">{label}</div>
      <div className={`text-sm text-white/90 break-words ${mono ? "font-mono" : ""}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function DetailOrderRow({ order }) {
  const { t } = useAdminI18n();
  return (
    <div
      className="grid gap-2 rounded-xl border border-white/8 bg-white/[0.03] px-3 py-3 text-xs sm:grid-cols-[1.1fr,1fr,0.9fr,0.8fr]"
      data-testid={`detail-order-${order.order_number}`}
    >
      <div>
        <div className="text-[10px] uppercase tracking-wider text-white/35">{t("customers.orders")}</div>
        <div className="mt-1 font-mono text-[#F58D4A]">{withPlaceholder(order.order_number)}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-white/35">{t("customers.date")}</div>
        <div className="mt-1 text-white/70">{formatAdminDate(order.created_at)}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-white/35">{t("customers.totalLabel")}</div>
        <div className="mt-1 text-white/90">${(order.total || 0).toFixed(2)}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-white/35">{t("customers.payment")}</div>
        <div className="mt-1 uppercase text-white/70">{withPlaceholder(order.payment_method)}</div>
      </div>
    </div>
  );
}

function CustomerDetailModal({ customer, onClose, onAction, busy }) {
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { t } = useAdminI18n();

  if (!customer) return null;

  const source = customer.customer || {};
  const identity = extractCustomerIdentity(source);
  const status = String(source.status || "active").toLowerCase();
  const isDeleted = ["deleted", "closed"].includes(status);
  const orders = customer.recent_orders || [];
  const lastLogin = source.last_login_at || source.lastLoginAt || source.last_login;
  const totalOrderValue = Number(source.total_order_value ?? customer.total_order_value ?? 0);
  const lastOrderAt = source.last_order_at || customer.last_order_at;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur"
      onClick={onClose}
      data-testid="customer-detail-modal"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#0c0c0c] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#0c0c0c]/95 px-5 py-4 backdrop-blur sm:px-6">
          <div className="min-w-0">
            <div className="text-[11px] tracking-[0.25em] uppercase text-white/40">{t("customers.detailTitle")}</div>
            <h3 className="mt-1 truncate font-display text-lg font-bold text-white" data-testid="customer-detail-name">
              {identity.displayName || identity.customerId || source.email}
            </h3>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {identity.customerId && <CustomerIdPill value={identity.customerId} />}
              <StatusBadge status={source.status} />
              <VerificationBadge verification={identity.verification} />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-white/60 transition hover:bg-white/10 hover:text-white"
            data-testid="customer-detail-close"
            aria-label={t("shell.closeNav")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          <div className="space-y-6">
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field label={t("customers.customerId")} value={identity.customerId || ADMIN_PLACEHOLDER} mono testid="detail-id" />
              <Field label={t("customers.fullName")} value={identity.displayName || ADMIN_PLACEHOLDER} testid="detail-name" />
              <Field label={t("customers.email")} value={identity.email || ADMIN_PLACEHOLDER} testid="detail-email" />
              <Field label={t("customers.phone")} value={identity.phone || ADMIN_PLACEHOLDER} testid="detail-phone" />
              <Field label={t("customers.accountSource")} value={accountSourceLabel(source.account_source, t)} testid="detail-source" />
              <Field label={t("customers.language")} value={formatLanguageLabel(source.lang || source.language)} testid="detail-language" />
              <Field label={t("customers.verification")} value={<VerificationBadge verification={identity.verification} />} testid="detail-verification" />
              <Field label={t("customers.address")} value={identity.address || ADMIN_PLACEHOLDER} full testid="detail-address" />
              <Field label={t("customers.city")} value={identity.city || ADMIN_PLACEHOLDER} testid="detail-city" />
              <Field label={t("customers.country")} value={identity.country || ADMIN_PLACEHOLDER} testid="detail-country" />
              <Field label={t("customers.deliveryNotes")} value={identity.deliveryNotes || ADMIN_PLACEHOLDER} full testid="detail-notes" />
              <Field label={t("customers.registered")} value={formatAdminDate(source.created_at)} testid="detail-created" />
              <Field label={t("customers.lastLogin")} value={formatAdminDate(lastLogin)} testid="detail-last-login" />
              <Field label={t("customers.lastOrder")} value={formatAdminDate(lastOrderAt)} testid="detail-last-order" />
              <Field label={t("customers.status")} value={<StatusBadge status={source.status} />} testid="detail-status" />
              <Field label={t("customers.orderCount")} value={String(customer.total_orders || source.total_orders || 0)} testid="detail-orders-count" />
              <Field label={t("customers.totalSpent")} value={`$${totalOrderValue.toFixed(2)}`} testid="detail-total-spent" />
            </section>

            <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <h4 className="text-sm font-semibold text-white/85">{t("customers.adminActions")}</h4>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy || isDeleted}
                  onClick={() => onAction("status", ["frozen", "suspended"].includes(status) ? "active" : "frozen")}
                  className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition disabled:opacity-50 ${
                    ["frozen", "suspended"].includes(status)
                      ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                      : "border-amber-500/30 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"
                  }`}
                  data-testid="customer-action-toggle-freeze"
                >
                  {["frozen", "suspended"].includes(status) ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldOff className="h-3.5 w-3.5" />}
                  {status === "frozen"
                    ? t("customers.unfreezeAccountAction", "Unfreeze account")
                    : status === "suspended"
                      ? t("customers.reactivate")
                      : t("customers.freeze")}
                </button>

                {status !== "active" && !isDeleted ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onAction("status", "active")}
                    className="inline-flex h-9 items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/15 px-3 text-xs font-semibold text-emerald-300 transition hover:bg-emerald-500/25 disabled:opacity-50"
                    data-testid="customer-action-activate"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {t("customers.activate", "Active")}
                  </button>
                ) : null}

                <button
                  type="button"
                  disabled={busy || isDeleted}
                  onClick={() => onAction("status", "inactive")}
                  className="inline-flex h-9 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 text-xs font-semibold text-white/80 transition hover:bg-white/10 disabled:opacity-50"
                  data-testid="customer-action-mark-inactive"
                >
                  {t("customers.markInactive")}
                </button>

                {!isDeleted ? (
                  !confirmDelete ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setConfirmReset(false);
                        setConfirmDelete(true);
                      }}
                      className="inline-flex h-9 items-center gap-1.5 rounded-full border border-red-500/35 bg-red-500/15 px-3 text-xs font-semibold text-red-300 transition hover:bg-red-500/25 disabled:opacity-50"
                      data-testid="customer-action-delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {t("customers.deleteAccountAction")}
                    </button>
                  ) : (
                    <div className="inline-flex min-h-9 flex-wrap items-center gap-2 rounded-2xl border border-red-500/40 bg-red-500/10 px-2 py-2 text-xs">
                      <span className="text-red-100/90">
                        {t("customers.deleteConfirm", "Delete this customer account? Orders are kept.")}
                      </span>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setConfirmDelete(false);
                          onAction("status", "deleted");
                        }}
                        className="h-7 rounded-full bg-red-500 px-3 text-xs font-semibold text-white transition hover:bg-red-600 disabled:opacity-50"
                        data-testid="customer-action-delete-confirm"
                      >
                        {t("customers.deleteAccountAction")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(false)}
                        className="h-7 rounded-full bg-white/10 px-3 text-xs text-white/70 transition hover:bg-white/20"
                        data-testid="customer-action-delete-cancel"
                      >
                        {t("customers.cancel")}
                      </button>
                    </div>
                  )
                ) : null}

                {!confirmReset ? (
                  <button
                    type="button"
                    disabled={busy || isDeleted}
                    onClick={() => {
                      setConfirmDelete(false);
                      setConfirmReset(true);
                    }}
                    className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[#F37021] px-3 text-xs font-semibold text-white transition hover:bg-[#d85f15] disabled:opacity-50"
                    data-testid="customer-action-send-reset"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    {t("customers.sendResetEmail")}
                  </button>
                ) : (
                  <div className="inline-flex min-h-9 flex-wrap items-center gap-2 rounded-2xl border border-[#F37021]/40 bg-[#F37021]/10 px-2 py-2 text-xs">
                    <span className="text-white/80">
                      {t("customers.sendResetPrompt", "Send reset link to {email}?").replace("{email}", identity.email || source.email || ADMIN_PLACEHOLDER)}
                    </span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setConfirmReset(false);
                        onAction("reset");
                      }}
                      className="h-7 rounded-full bg-[#F37021] px-3 text-xs font-semibold text-white transition hover:bg-[#d85f15] disabled:opacity-50"
                      data-testid="customer-action-send-reset-confirm"
                    >
                      {t("customers.yesSend")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmReset(false)}
                      className="h-7 rounded-full bg-white/10 px-3 text-xs text-white/70 transition hover:bg-white/20"
                      data-testid="customer-action-send-reset-cancel"
                    >
                      {t("customers.cancel")}
                    </button>
                  </div>
                )}
              </div>
              <p className="mt-3 text-[11px] text-white/40">
                {t("customers.resetNotice")}
              </p>
            </section>

            <section>
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-white/85">
                  {t("customers.recentOrders")} ({customer.total_orders || 0})
                </h4>
                <div className="text-[11px] text-white/38">
                  {t("customers.lastLoginNote")}
                </div>
              </div>
              {orders.length === 0 ? (
                <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-5 text-sm text-white/55">
                  {t("customers.noOrdersLinked")}
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  {orders.map((order) => (
                    <DetailOrderRow key={order.id || order.order_number} order={order} />
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, value, tone, testid }) {
  const tones = {
    emerald: "border-emerald-500/30 text-emerald-300",
    red: "border-red-500/30 text-red-300",
  };

  return (
    <div
      className={`rounded-full border bg-white/[0.03] px-3 py-1.5 text-xs ${tones[tone] || "border-white/15 text-white/70"}`}
      data-testid={testid}
    >
      <span className="me-1 opacity-60">{label}</span>
      <b>{value}</b>
    </div>
  );
}

function CustomerMobileCard({ customer, onOpen }) {
  const { t } = useAdminI18n();
  const identity = extractCustomerIdentity(customer);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="admin-panel-soft block w-full p-4 text-left transition hover:bg-white/[0.04] lg:hidden"
      data-testid={`customer-card-${customer.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate text-sm font-semibold text-white">
              {identity.displayName || ADMIN_PLACEHOLDER}
            </div>
            {identity.customerId && <CustomerIdPill value={identity.customerId} />}
          </div>
          <div className="mt-2 text-xs text-white/68">{identity.email || ADMIN_PLACEHOLDER}</div>
          <div className="mt-1 text-xs text-white/52">{identity.phone || ADMIN_PLACEHOLDER}</div>
          <div className="mt-1 text-xs text-white/42">
            {[identity.city, identity.country].filter(Boolean).join(" / ") || ADMIN_PLACEHOLDER}
          </div>
        </div>
        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-white/35" />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <StatusBadge status={customer.status} />
        <VerificationBadge verification={identity.verification} />
        <span className="text-[11px] text-white/35">{accountSourceLabel(customer.account_source, t)}</span>
        <span className="text-[11px] text-white/35">{customer.total_orders || 0} {t("customers.orders").toLowerCase()}</span>
        <span className="text-[11px] text-white/35">${Number(customer.total_order_value || 0).toFixed(2)} {t("customers.totalLabel").toLowerCase()}</span>
      </div>
    </button>
  );
}

function AdminCustomersPageInner() {
  const { apiFetch } = useAdminAuth();
  const { t } = useAdminI18n();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [deletionLoading, setDeletionLoading] = useState(true);
  const [deletionBusyId, setDeletionBusyId] = useState(null);
  const [deletionDecisionNotes, setDeletionDecisionNotes] = useState({});
  const [frozenCustomers, setFrozenCustomers] = useState([]);
  const [statusBusyId, setStatusBusyId] = useState(null);
  const [reviewSectionsOpen, setReviewSectionsOpen] = useState({});
  const [notificationDraft, setNotificationDraft] = useState({
    title: "",
    body: "",
    audience: "all_users",
    code: "",
    requestId: "",
  });
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(true);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (statusFilter) params.set("status", statusFilter);
      const res = await apiFetch(`/admin/registered-customers${params.toString() ? `?${params}` : ""}`);
      const data = await readJsonSafe(res);
      if (!res.ok) throw new Error(extractApiMessage(res, data, "Failed to load customers"));
      setCustomers(Array.isArray(data?.customers) ? data.customers : []);
    } catch (e) {
      const message = e?.message || "Failed to load customers";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, q, statusFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const fetchDeletionRequests = useCallback(async () => {
    setDeletionLoading(true);
    try {
      const res = await apiFetch("/admin/account-deletion-requests");
      const data = await readJsonSafe(res);
      if (!res.ok) throw new Error(extractApiMessage(res, data, "Failed to load deletion requests"));
      setDeletionRequests(Array.isArray(data?.requests) ? data.requests : []);
    } catch (e) {
      toast.error(e?.message || "Failed to load deletion requests");
    } finally {
      setDeletionLoading(false);
    }
  }, [apiFetch]);

  const fetchFrozenCustomers = useCallback(async () => {
    try {
      const res = await apiFetch("/admin/registered-customers?status=frozen");
      const data = await readJsonSafe(res);
      if (!res.ok) throw new Error(extractApiMessage(res, data, "Failed to load frozen customers"));
      setFrozenCustomers(Array.isArray(data?.customers) ? data.customers : []);
    } catch (e) {
      toast.error(e?.message || "Failed to load frozen customers");
    }
  }, [apiFetch]);

  const refreshReviewSections = useCallback(async () => {
    await Promise.all([fetchDeletionRequests(), fetchFrozenCustomers()]);
  }, [fetchDeletionRequests, fetchFrozenCustomers]);

  const fetchNotificationLogs = useCallback(async () => {
    setNotificationLoading(true);
    try {
      const res = await apiFetch("/admin/app-notifications/logs");
      const data = await readJsonSafe(res);
      if (!res.ok) throw new Error(extractApiMessage(res, data, "Failed to load notification logs"));
      setNotificationLogs(Array.isArray(data?.logs) ? data.logs : []);
    } catch (e) {
      toast.error(e?.message || "Failed to load notification logs");
    } finally {
      setNotificationLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    refreshReviewSections();
    fetchNotificationLogs();
  }, [refreshReviewSections, fetchNotificationLogs]);

  const loadDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await apiFetch(`/admin/registered-customers/${id}`);
      const data = await readJsonSafe(res);
      if (!res.ok) throw new Error(extractApiMessage(res, data, "Failed to load customer"));
      setDetail(data);
    } catch (e) {
      toast.error(e?.message || "Failed to load customer");
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const handleAction = async (kind, value) => {
    if (!detail?.customer?.id) return;
    const id = detail.customer.id;
    setActionBusy(true);
    try {
      if (kind === "status") {
        const res = await apiFetch(`/admin/registered-customers/${id}/status`, {
          method: "PATCH",
          body: JSON.stringify({ status: value }),
        });
        const data = await readJsonSafe(res);
        if (!res.ok) throw new Error(extractApiMessage(res, data, "Update failed"));
        toast.success(
          value === "deleted"
            ? t("customers.deleteSuccess", "Customer account deleted")
            : `Status set to "${value}"`,
        );
        setDetail((current) => current ? { ...current, customer: { ...current.customer, ...data } } : current);
        setCustomers((list) => list.map((c) => (c.id === id ? { ...c, ...data } : c)));
        if (value === "deleted") {
          setSelectedId(null);
          await fetchList();
        }
      } else if (kind === "reset") {
        const res = await apiFetch(`/admin/registered-customers/${id}/send-reset-email`, {
          method: "POST",
        });
        const data = await readJsonSafe(res);
        if (!res.ok) throw new Error(extractApiMessage(res, data, "Send failed"));
        toast.success(`Reset email sent to ${data?.sent_to} (valid ${data?.expires_in_minutes} min)`);
      }
    } catch (e) {
      toast.error(e?.message || "Action failed");
    } finally {
      setActionBusy(false);
    }
  };

  const handleStandaloneStatus = async (customerId, status) => {
    if (!customerId) return;
    setStatusBusyId(customerId);
    try {
      const res = await apiFetch(`/admin/registered-customers/${customerId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      const data = await readJsonSafe(res);
      if (!res.ok) throw new Error(extractApiMessage(res, data, "Update failed"));
      toast.success(t("customers.deletionStatusUpdated", "Customer status updated"));
      setCustomers((list) => list.map((customer) => (customer.id === customerId ? { ...customer, ...data } : customer)));
      await fetchFrozenCustomers();
    } catch (e) {
      toast.error(e?.message || "Update failed");
    } finally {
      setStatusBusyId(null);
    }
  };

  const stats = useMemo(() => {
    const total = customers.length;
    const active = customers.filter((c) => (c.status || "active") === "active").length;
    const suspended = customers.filter((c) => ["suspended", "frozen"].includes(c.status)).length;
    return { total, active, suspended };
  }, [customers]);

  const deletionSections = useMemo(() => {
    const groups = {
      pending: [],
      completed: [],
      rejected: [],
    };
    deletionRequests.forEach((item) => {
      const status = String(item.status || "pending").toLowerCase();
      if (status === "completed" || status === "deleted") groups.completed.push(item);
      else if (status === "rejected") groups.rejected.push(item);
      else groups.pending.push(item);
    });
    return [
      { key: "pending", items: groups.pending, kind: "requests" },
      { key: "frozen", items: frozenCustomers, kind: "customers" },
      { key: "completed", items: groups.completed, kind: "requests" },
      { key: "rejected", items: groups.rejected, kind: "requests" },
    ];
  }, [deletionRequests, frozenCustomers]);

  const toggleReviewSection = (key, count) => {
    if (count === 0) return;
    setReviewSectionsOpen((current) => ({ ...current, [key]: !current[key] }));
  };

  const handleDeletionStatus = async (requestId, status, action = status) => {
    const adminNote = String(deletionDecisionNotes[requestId] || "").trim();
    if (action === "reject" && !adminNote) {
      toast.error(t("customers.rejectionReasonRequired", "Please enter a rejection reason before rejecting the request."));
      return;
    }
    setDeletionBusyId(requestId);
    try {
      const res = await apiFetch(`/admin/account-deletion-requests/${requestId}`, {
        method: "PATCH",
        body: JSON.stringify({ status, action, admin_note: adminNote || undefined }),
      });
      const data = await readJsonSafe(res);
      if (!res.ok) throw new Error(extractApiMessage(res, data, "Failed to update deletion request"));
      setDeletionRequests((list) => list.map((item) => (item.id === requestId ? data.request : item)));
      if (data?.account_remains_frozen) {
        toast.warning(t("customers.accountRemainsFrozen", "Request rejected, but the account remains frozen because it was frozen for another reason."));
      } else {
        toast.success(t("customers.deletionStatusUpdated", "Deletion request updated"));
      }
      setDeletionDecisionNotes((current) => ({ ...current, [requestId]: "" }));
      await Promise.all([fetchList(), fetchFrozenCustomers()]);
    } catch (e) {
      toast.error(e?.message || "Failed to update deletion request");
    } finally {
      setDeletionBusyId(null);
    }
  };

  const handleRequestNotificationCode = async () => {
    setNotificationBusy(true);
    try {
      const res = await apiFetch("/admin/app-notifications/request-code", {
        method: "POST",
        body: JSON.stringify({
          title: notificationDraft.title,
          body: notificationDraft.body,
          audience: notificationDraft.audience,
        }),
      });
      const data = await readJsonSafe(res);
      if (!res.ok) throw new Error(extractApiMessage(res, data, "Failed to request verification code"));
      setNotificationDraft((current) => ({ ...current, requestId: data.request_id, code: "" }));
      toast.success(t("customers.verificationCodeSent", "Verification code sent to {email}").replace("{email}", data.sent_to || ADMIN_PLACEHOLDER));
      fetchNotificationLogs();
    } catch (e) {
      toast.error(e?.message || "Failed to request verification code");
    } finally {
      setNotificationBusy(false);
    }
  };

  const handleSendNotification = async () => {
    setNotificationBusy(true);
    try {
      const res = await apiFetch("/admin/app-notifications/send", {
        method: "POST",
        body: JSON.stringify({
          request_id: notificationDraft.requestId,
          code: normalizeVerificationCode(notificationDraft.code),
          title: notificationDraft.title,
          body: notificationDraft.body,
          audience: notificationDraft.audience,
        }),
      });
      const data = await readJsonSafe(res);
      if (!res.ok) throw new Error(extractApiMessage(res, data, "Push provider/tokens are not configured yet."));
      toast.success(data?.message || "Notification send accepted");
    } catch (e) {
      toast.error(e?.message || "Push provider/tokens are not configured yet.");
    } finally {
      setNotificationBusy(false);
      fetchNotificationLogs();
    }
  };

  return (
    <AdminShell
      title={t("customers.title")}
      description={t("customers.description")}
    >
      <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#F37021]">
              <Users className="h-3.5 w-3.5" /> {t("customers.registeredEyebrow")}
            </div>
            <h1 className="mt-1 font-display text-2xl font-bold">{t("customers.subscribers")}</h1>
            <p className="mt-1 text-sm text-white/50">
              {t("customers.intro")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatPill label={t("customers.total")} value={stats.total} testid="stat-total" />
            <StatPill label={t("customers.active")} value={stats.active} testid="stat-active" tone="emerald" />
            <StatPill label={t("customers.restrictedAccounts", "Frozen / suspended")} value={stats.suspended} testid="stat-suspended" tone="red" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchList()}
              placeholder={t("customers.searchPlaceholder")}
              className="h-10 w-full rounded-full border border-white/10 bg-black/40 ps-9 pe-3 text-sm focus:border-[#F37021] focus:outline-none"
              data-testid="customers-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 rounded-full border border-white/10 bg-black/40 px-3 text-sm focus:border-[#F37021] focus:outline-none"
            data-testid="customers-status-filter"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.id || "all"} value={option.id}>{t(`customers.${option.labelKey}`, option.fallback)}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={fetchList}
            disabled={loading}
            className="inline-flex h-10 items-center gap-1.5 rounded-full bg-[#F37021] px-4 text-sm font-semibold text-white transition hover:bg-[#d85f15] disabled:opacity-50"
            data-testid="customers-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {t("customers.refresh")}
          </button>
        </div>

        {error && !loading && (
          <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        <section className="space-y-3" data-testid="customers-table">
          {loading ? (
            <div className="admin-panel px-6 py-10 text-center text-sm text-white/50">{t("customers.loadingCustomers")}</div>
          ) : customers.length === 0 ? (
            <div className="admin-panel px-6 py-10 text-center text-sm text-white/50" data-testid="customers-empty">
              {t("customers.noCustomers")}
            </div>
          ) : (
            <>
              <div className="space-y-3 lg:hidden">
                {customers.map((customer) => (
                  <CustomerMobileCard
                    key={customer.id}
                    customer={customer}
                    onOpen={() => setSelectedId(customer.id)}
                  />
                ))}
              </div>

              <div className="admin-panel hidden overflow-hidden lg:block">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/50">
                      <tr>
                        <th className="px-4 py-3 text-left">{t("customers.customerId")}</th>
                        <th className="px-4 py-3 text-left">{t("customers.fullName")}</th>
                        <th className="px-4 py-3 text-left">{t("customers.email")}</th>
                        <th className="px-4 py-3 text-left">{t("customers.phone")}</th>
                        <th className="px-4 py-3 text-left">{t("customers.accountSource")}</th>
                        <th className="px-4 py-3 text-left">{t("customers.verification")}</th>
                        <th className="px-4 py-3 text-left">{t("customers.status")}</th>
                        <th className="px-4 py-3 text-right">{t("customers.orders")}</th>
                        <th className="px-4 py-3 text-right">{t("customers.totalSpent")}</th>
                        <th className="px-4 py-3 text-left">{t("customers.registered")}</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map((customer) => {
                        const identity = extractCustomerIdentity(customer);
                        return (
                          <tr
                            key={customer.id}
                            className="cursor-pointer border-t border-white/5 hover:bg-white/[0.03]"
                            onClick={() => setSelectedId(customer.id)}
                            data-testid={`customer-row-${customer.id}`}
                          >
                            <td className="px-4 py-3 font-mono text-xs text-[#F58D4A]">
                              {identity.customerId || ADMIN_PLACEHOLDER}
                            </td>
                            <td className="px-4 py-3">{identity.displayName || ADMIN_PLACEHOLDER}</td>
                            <td className="px-4 py-3 text-white/82">{identity.email || ADMIN_PLACEHOLDER}</td>
                            <td className="px-4 py-3 text-white/68">{identity.phone || ADMIN_PLACEHOLDER}</td>
                            <td className="px-4 py-3 text-white/62">{accountSourceLabel(customer.account_source, t)}</td>
                            <td className="px-4 py-3"><VerificationBadge verification={identity.verification} /></td>
                            <td className="px-4 py-3"><StatusBadge status={customer.status} /></td>
                            <td className="px-4 py-3 text-right text-white/82">{customer.total_orders || 0}</td>
                            <td className="px-4 py-3 text-right text-white/60">${Number(customer.total_order_value || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-xs text-white/50">{formatAdminDate(customer.created_at)}</td>
                            <td className="px-4 py-3 text-right">
                              <ChevronRight className="inline h-4 w-4 text-white/40" />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </section>

        <section className="admin-panel space-y-4 p-5" data-testid="deletion-requests-panel">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#F37021]">
                <Trash2 className="h-3.5 w-3.5" /> {t("customers.deletionRequests")}
              </div>
              <h2 className="mt-1 text-xl font-semibold text-white">{t("customers.deletionQueue")}</h2>
              <p className="mt-1 max-w-3xl text-sm text-white/52">
                {t("customers.deletionIntro")}
              </p>
            </div>
            <button
              type="button"
              onClick={refreshReviewSections}
              disabled={deletionLoading}
              className="inline-flex h-10 items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white/82 transition hover:bg-white/10 disabled:opacity-50"
              data-testid="deletion-requests-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${deletionLoading ? "animate-spin" : ""}`} />
              {t("customers.refresh")}
            </button>
          </div>

          {deletionLoading ? (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-5 text-sm text-white/55">{t("customers.loadingDeletionRequests")}</div>
          ) : (
            <div className="space-y-2">
              {deletionSections.map((section) => {
                const isOpen = Boolean(reviewSectionsOpen[section.key]) && section.items.length > 0;
                return (
                  <div key={section.key} className="space-y-2" data-testid={`deletion-section-${section.key}`}>
                    <button
                      type="button"
                      onClick={() => toggleReviewSection(section.key, section.items.length)}
                      disabled={section.items.length === 0}
                      aria-expanded={isOpen}
                      className="flex w-full items-center justify-between gap-3 rounded-xl border border-white/8 bg-black/20 px-3 py-3 text-start transition hover:bg-white/[0.04] disabled:cursor-default disabled:opacity-65"
                      data-testid={`deletion-section-toggle-${section.key}`}
                    >
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/65">
                        {t(`customers.deletionSections.${section.key}`, section.key)} ({section.items.length})
                      </span>
                      <ChevronRight className={`h-4 w-4 text-white/45 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    </button>

                    {isOpen && section.kind === "customers" ? (
                      <div className="space-y-2">
                        {section.items.map((customer) => {
                          const identity = extractCustomerIdentity(customer);
                          return (
                            <article key={customer.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`frozen-customer-${customer.id}`}>
                              <button type="button" onClick={() => setSelectedId(customer.id)} className="min-w-0 flex-1 text-start">
                                <div className="flex flex-wrap items-center gap-2">
                                  <div className="font-semibold text-white">{identity.displayName || ADMIN_PLACEHOLDER}</div>
                                  <StatusBadge status={customer.status} />
                                  <CustomerIdPill value={identity.customerId} />
                                </div>
                                <div className="mt-2 text-sm text-white/60">{withPlaceholder(identity.email)} · {withPlaceholder(identity.phone)}</div>
                              </button>
                              <button
                                type="button"
                                disabled={statusBusyId === customer.id}
                                onClick={() => handleStandaloneStatus(customer.id, "active")}
                                className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/12 px-3 py-2 text-xs font-semibold text-emerald-200 transition hover:bg-emerald-500/20 disabled:opacity-40"
                                data-testid={`customer-unfreeze-${customer.id}`}
                              >
                                <ShieldCheck className="h-3.5 w-3.5" />
                                {t("customers.unfreezeAccountAction", "Unfreeze account")}
                              </button>
                            </article>
                          );
                        })}
                      </div>
                    ) : null}

                    {isOpen && section.kind === "requests" ? (
                      <div className="space-y-2">
                        {section.items.map((item) => {
                          const identity = extractCustomerIdentity(item);
                          const finalised = ["completed", "deleted", "rejected"].includes(String(item.status || "").toLowerCase());
                          return (
                            <article key={item.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`deletion-request-${item.id}`}>
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <div className="font-semibold text-white">{identity.displayName || ADMIN_PLACEHOLDER}</div>
                                    <RequestStatusBadge status={item.status} />
                                    <CustomerIdPill value={identity.customerId} />
                                  </div>
                                  <div className="mt-2 grid gap-2 text-sm text-white/68 md:grid-cols-2">
                                    <div>{t("customers.email")}: {withPlaceholder(identity.email)}</div>
                                    <div>{t("customers.phone")}: {withPlaceholder(identity.phone)}</div>
                                    <div>{t("customers.reason")}: {deletionReasonLabel(item, t)}</div>
                                    <div>{t("customers.requested")}: {formatAdminDate(item.requested_at || item.requested_at_iso)}</div>
                                    <div className="md:col-span-2">{t("customers.note")}: {withPlaceholder(item.note)}</div>
                                    {item.admin_note ? <div className="md:col-span-2">{t("customers.adminDecisionNote", "Admin decision note")}: {item.admin_note}</div> : null}
                                  </div>
                                </div>
                                {!finalised ? (
                                  <div className="w-full space-y-2 sm:w-auto sm:min-w-[280px]">
                                    <label className="block space-y-1">
                                      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/45">
                                        {t("customers.rejectionReason", "Rejection reason")}
                                      </span>
                                      <textarea
                                        rows={2}
                                        value={deletionDecisionNotes[item.id] || ""}
                                        onChange={(event) => setDeletionDecisionNotes((current) => ({ ...current, [item.id]: event.target.value }))}
                                        maxLength={500}
                                        className="w-full rounded-xl border border-white/10 bg-black/25 px-3 py-2 text-sm text-white outline-none transition focus:border-[#F37021]/60"
                                        placeholder={t("customers.rejectionReasonPlaceholder", "Required when rejecting")}
                                        data-testid={`deletion-rejection-note-${item.id}`}
                                      />
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        disabled={deletionBusyId === item.id}
                                        onClick={() => handleDeletionStatus(item.id, "completed", "delete")}
                                        className="rounded-full border border-red-500/30 bg-red-500/12 px-3 py-2 text-xs font-semibold text-red-300 transition hover:bg-red-500/20 disabled:opacity-40"
                                        data-testid={`deletion-delete-${item.id}`}
                                      >
                                        {t("customers.deleteAccountAction")}
                                      </button>
                                      <button
                                        type="button"
                                        disabled={deletionBusyId === item.id}
                                        onClick={() => handleDeletionStatus(item.id, "rejected", "reject")}
                                        className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white/75 transition hover:bg-white/10 disabled:opacity-40"
                                        data-testid={`deletion-reject-${item.id}`}
                                      >
                                        {t("customers.reject")}
                                      </button>
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="admin-panel space-y-5 p-5" data-testid="app-notifications-panel">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F37021]/16 text-[#F58D4A]">
              <BellRing className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#F37021]">{t("customers.notifications")}</div>
              <h2 className="mt-1 text-xl font-semibold text-white">{t("customers.verifiedSendControl")}</h2>
              <p className="mt-1 text-sm text-white/52">
                {t("customers.pushIntro")}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-[#F37021]/20 bg-[#F37021]/8 p-4 text-sm text-white/80">
            <div className="flex items-start gap-2">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[#F58D4A]" />
              <div className="space-y-2">
                <div>{t("customers.pushWarning")}</div>
                <div dir="rtl" className="text-right">{t("customers.pushWarningAr")}</div>
              </div>
            </div>
          </div>

          <div className="grid gap-5 xl:grid-cols-[1.2fr,0.8fr]">
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-2 md:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-white/45">{t("customers.notificationTitle")}</span>
                  <input
                    type="text"
                    value={notificationDraft.title}
                    onChange={(e) => setNotificationDraft((current) => ({ ...current, title: e.target.value }))}
                    className="h-11 w-full rounded-2xl border border-white/10 bg-black/35 px-4 text-sm text-white focus:border-[#F37021] focus:outline-none"
                    data-testid="notification-title"
                  />
                </label>
                <label className="space-y-2 md:col-span-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-white/45">{t("customers.notificationBody")}</span>
                  <textarea
                    value={notificationDraft.body}
                    onChange={(e) => setNotificationDraft((current) => ({ ...current, body: e.target.value }))}
                    className="min-h-[140px] w-full rounded-2xl border border-white/10 bg-black/35 px-4 py-3 text-sm text-white focus:border-[#F37021] focus:outline-none"
                    data-testid="notification-body"
                  />
                </label>
                <label className="space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-white/45">{t("customers.audience")}</span>
                  <select
                    value={notificationDraft.audience}
                    onChange={(e) => setNotificationDraft((current) => ({ ...current, audience: e.target.value }))}
                    className="h-11 w-full rounded-2xl border border-white/10 bg-black/35 px-4 text-sm text-white focus:border-[#F37021] focus:outline-none"
                    data-testid="notification-audience"
                  >
                    <option value="all_users">{t("customers.allUsers")}</option>
                  </select>
                </label>
                <label className="space-y-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-white/45">{t("customers.verificationCode")}</span>
                  <input
                    type="text"
                    value={notificationDraft.code}
                    onChange={(e) => setNotificationDraft((current) => ({ ...current, code: normalizeVerificationCode(e.target.value) }))}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    dir="ltr"
                    className="h-11 w-full rounded-2xl border border-white/10 bg-black/35 px-4 text-sm tracking-[0.2em] text-white focus:border-[#F37021] focus:outline-none"
                    placeholder="000000"
                    data-testid="notification-code"
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleRequestNotificationCode}
                  disabled={notificationBusy || !notificationDraft.title.trim() || !notificationDraft.body.trim()}
                  className="inline-flex h-11 items-center gap-2 rounded-full bg-[#F37021] px-4 text-sm font-semibold text-white transition hover:bg-[#d85f15] disabled:opacity-50"
                  data-testid="notification-request-code"
                >
                  <Mail className="h-4 w-4" />
                  {t("customers.requestVerificationCode")}
                </button>
                <button
                  type="button"
                  onClick={handleSendNotification}
                  disabled={notificationBusy || !notificationDraft.requestId || normalizeVerificationCode(notificationDraft.code).length !== 6}
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 text-sm font-semibold text-white/82 transition hover:bg-white/10 disabled:opacity-50"
                  data-testid="notification-send"
                >
                  <Send className="h-4 w-4" />
                  {t("customers.verifyAndSend")}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="text-[11px] uppercase tracking-[0.24em] text-white/35">{t("customers.preview")}</div>
                <div className="mt-3 rounded-2xl border border-white/10 bg-[#080808] p-4">
                  <div className="text-sm font-semibold text-white">{notificationDraft.title || t("customers.notificationTitleFallback")}</div>
                  <div className="mt-2 whitespace-pre-wrap text-sm leading-6 text-white/72">
                    {notificationDraft.body || t("customers.notificationBodyFallback")}
                  </div>
                  <div className="mt-3 text-[11px] uppercase tracking-wider text-[#F7A76F]">
                    {t("customers.audience")}: {notificationDraft.audience === "all_users" ? t("customers.allUsers") : notificationDraft.audience}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-white/35">{t("customers.recentAttempts")}</div>
                    <div className="mt-1 text-sm text-white/65">{t("customers.recentAttemptsBody")}</div>
                  </div>
                  <button
                    type="button"
                    onClick={fetchNotificationLogs}
                    disabled={notificationLoading}
                    className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white/80 transition hover:bg-white/10 disabled:opacity-50"
                    data-testid="notification-logs-refresh"
                  >
                    {t("customers.refresh")}
                  </button>
                </div>

                {notificationLoading ? (
                  <div className="mt-3 text-sm text-white/55">{t("customers.loadingNotificationLogs")}</div>
                ) : notificationLogs.length === 0 ? (
                  <div className="mt-3 text-sm text-white/55">{t("customers.noNotificationAttempts")}</div>
                ) : (
                  <div className="mt-3 space-y-3">
                    {notificationLogs.map((log) => (
                      <div key={log.id} className="rounded-2xl border border-white/10 bg-black/20 p-3" data-testid={`notification-log-${log.id}`}>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-semibold text-white">{withPlaceholder(log.title)}</div>
                          <RequestStatusBadge status={log.status} />
                        </div>
                        <div className="mt-2 text-xs leading-5 text-white/60">{withPlaceholder(log.body)}</div>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/42">
                          <span>{t("customers.audience")}: {withPlaceholder(log.audience)}</span>
                          <span>{t("customers.requested")}: {formatAdminDate(log.code_requested_at || log.created_at)}</span>
                          <span>{t("customers.verifiedAt")}: {formatAdminDate(log.verified_at)}</span>
                        </div>
                        <div className="mt-2 text-[11px] text-[#F7A76F]">{t("customers.provider")}: {withPlaceholder(log.provider_result)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {selectedId && (
        detailLoading ? (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur"
            data-testid="customer-detail-loading"
          >
            <div className="rounded-2xl border border-white/10 bg-[#0c0c0c] px-8 py-6 text-sm text-white/70">
              {t("customers.loadingCustomer")}
            </div>
          </div>
        ) : detail ? (
          <CustomerDetailModal
            customer={detail}
            onClose={() => setSelectedId(null)}
            onAction={handleAction}
            busy={actionBusy}
          />
        ) : null
      )}

    </AdminShell>
  );
}

export default function AdminCustomersPage() {
  return (
    <RequireAdmin permission="customers.manage">
      <AdminCustomersPageInner />
    </RequireAdmin>
  );
}
